import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Warning, CheckCircle } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { saveAuthData } from '@/utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const validateRegister = (data) => {
  const errors = {};
  if (!data.username || data.username.length < 3) {
    errors.username = 'Username must be at least 3 characters';
  }
  if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.email = 'Please enter a valid email';
  }
  if (!data.password || data.password.length < 8) {
    errors.password = 'Password must be at least 8 characters';
  }
  return errors;
};

export const RegisterForm = ({ onAuthSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [registerData, setRegisterData] = useState({ username: '', email: '', password: '' });
  const [errors, setErrors] = useState({});

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    const validationErrors = validateRegister(registerData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/register`, registerData);
      saveAuthData(response.data.access_token, response.data.user);
      toast.success('Account created successfully!');
      onAuthSuccess(response.data.user);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Registration failed';
      // Map server errors to specific fields
      if (detail.toLowerCase().includes('email')) {
        setErrors({ email: detail });
      } else if (detail.toLowerCase().includes('username')) {
        setErrors({ username: detail });
      } else if (detail.toLowerCase().includes('password')) {
        setErrors({ password: detail });
      } else {
        setErrors({ form: detail });
      }
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = registerData.password.length >= 8;

  return (
    <form onSubmit={handleRegister} className="space-y-4" noValidate>
      {errors.form && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="register-error">
          <Warning size={18} weight="fill" className="mt-0.5 flex-shrink-0" />
          <span>{errors.form}</span>
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="register-username">Username</Label>
        <Input
          id="register-username"
          type="text"
          placeholder="gamerPro123"
          value={registerData.username}
          onChange={(e) => {
            setRegisterData({ ...registerData, username: e.target.value });
            if (errors.username) setErrors({ ...errors, username: null });
          }}
          data-testid="register-username-input"
          className={`border-[#E4E4E7] ${errors.username ? 'border-red-500' : ''}`}
        />
        {errors.username && (
          <p className="text-xs text-red-600 flex items-center gap-1" data-testid="username-error">
            <Warning size={12} weight="fill" /> {errors.username}
          </p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="register-email">Email</Label>
        <Input
          id="register-email"
          type="email"
          placeholder="you@example.com"
          value={registerData.email}
          onChange={(e) => {
            setRegisterData({ ...registerData, email: e.target.value });
            if (errors.email) setErrors({ ...errors, email: null });
          }}
          data-testid="register-email-input"
          className={`border-[#E4E4E7] ${errors.email ? 'border-red-500' : ''}`}
        />
        {errors.email && (
          <p className="text-xs text-red-600 flex items-center gap-1" data-testid="email-error">
            <Warning size={12} weight="fill" /> {errors.email}
          </p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="register-password">Password</Label>
        <Input
          id="register-password"
          type="password"
          placeholder="At least 8 characters"
          value={registerData.password}
          onChange={(e) => {
            setRegisterData({ ...registerData, password: e.target.value });
            if (errors.password) setErrors({ ...errors, password: null });
          }}
          data-testid="register-password-input"
          className={`border-[#E4E4E7] ${errors.password ? 'border-red-500' : ''}`}
        />
        {errors.password ? (
          <p className="text-xs text-red-600 flex items-center gap-1" data-testid="password-error">
            <Warning size={12} weight="fill" /> {errors.password}
          </p>
        ) : registerData.password && (
          <p className={`text-xs flex items-center gap-1 ${passwordStrength ? 'text-green-600' : 'text-[#71717A]'}`}>
            {passwordStrength && <CheckCircle size={12} weight="fill" />}
            {passwordStrength ? 'Password looks good' : `${registerData.password.length}/8 characters`}
          </p>
        )}
      </div>
      <Button
        type="submit"
        className="w-full bg-[#0047FF] hover:bg-[#0037CC] text-white transition-transform hover:-translate-y-0.5"
        disabled={loading}
        data-testid="register-submit-button"
      >
        {loading ? 'Creating account...' : 'Create Account'}
      </Button>
    </form>
  );
};
